import React from 'react';
import 'react-toastify/dist/ReactToastify.css';
import {
    Button,
    Container,
    IconButton,
    Typography,
    Box,
    Stack, useMediaQuery, CircularProgress,
} from '@mui/material';
import HomeIcon from "@mui/icons-material/Home";
import CheckIcon from '@mui/icons-material/Check';
import WorkIcon from '@mui/icons-material/Work';
import { useTheme } from '@mui/material/styles';
import {LoadingPageView} from "../../../common/components/LoadingPageView";
import LogoErrorView from "../../../common/components/LogoErrorView";
import useReview from "../hooks/useReview";
import StudySessionView from "../components/StudySessionView";


function Review() {

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const {
        displaySessionSelectionDialog,
        session,
        loadingMessage,
        isLoadError,
        startNewSession,
        continueExistingSession,
        submitReviewItem,
        isSubmitReviewItemError,
        remainingItemCount,
        finishedItemCount,
        isResyncing,
        finishReviewSession
    } = useReview();

    if (isLoadError) {
        return (
            <LogoErrorView severity={"error"} message={`Could not load review session. Try refreshing the page.`} />
        )
    }

    if (isSubmitReviewItemError) {
        return (
            <LogoErrorView severity={"error"} message={`Could not submit review. Try refreshing the page.`}/>
        )
    }

    if (loadingMessage) {
        return <LoadingPageView message={loadingMessage}/>
    }

    if (displaySessionSelectionDialog) {
        return sessionSelectionDialogView();
    }

    if (!session) {
        return (
            <LogoErrorView severity={"error"} message={`Could not load review session. Try refreshing the page.`}/>
        )
    }

    return (
        <>
            <StudySessionView items={session.items} words={session.words} onItemReviewed={submitReviewItem}/>
            {topLeftActionRowView()}
            {isResyncing && resyncView()}
        </>
    )

    function topLeftActionRowView() {
        return (
            <Stack direction="row" alignItems="center" spacing={2} sx={{position: "absolute", top: 3, left: 1}}>
                <IconButton title="Finish reviews" color={"inherit"} onClick={finishReviewSession}>
                    <HomeIcon/>
                </IconButton>
                <div title="Remaining items">
                    <WorkIcon/>
                    <span>{remainingItemCount}</span>
                </div>
                <div title="Completed items">
                    <CheckIcon/>
                    <span>{finishedItemCount}</span>
                </div>
            </Stack>
        )
    }

    function resyncView() {
        return (
            <Stack
                direction="row"
                alignItems="center"
                spacing={1}
                sx={{
                    position: "fixed",
                    bottom: 12,
                    left: 12,
                    px: 1.5,
                    py: 1,
                    borderRadius: 2,
                    backgroundColor: "rgba(255, 255, 255, 0)",
                    // boxShadow: 1,
                    zIndex: 1300
                }}
            >
                <CircularProgress size={30} sx={{color: theme.palette.secondary.dark}}/>
                <Typography variant="caption" sx={{color: theme.palette.text.primary}}>
                    Resyncing session
                </Typography>
            </Stack>
        )
    }

    function sessionSelectionDialogView() {
        return (
            <Container disableGutters
                       maxWidth={false}
                       sx={{height: '100vh', display: "flex", flexDirection: "column", alignItems: "center", position: "relative", bgcolor: theme.palette.background.default}}>
                <Box sx={{display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100vh', padding: 3, textAlign: 'center', width: isMobile ? "100%" : 500}}>
                    <Typography variant="h6" sx={{color: theme.palette.text.primary}} gutterBottom>
                        Continue your last session?
                    </Typography>
                    <Box display="flex" justifyContent="space-between" sx={{ mt: 2, gap: 2, width: "100%" }}>
                        <Button
                            variant="contained"
                            onClick={continueExistingSession}
                            size={"large"}
                            sx={{
                                mt: 2,
                                borderRadius: 3,
                                width: "40%",
                                backgroundColor: theme.palette.secondary.main,
                                color: theme.palette.secondary.contrastText,
                            }}
                        >
                            Continue
                        </Button>
                        <Button
                            variant="contained"
                            onClick={startNewSession}
                            size={"large"}
                            sx={{
                                mt: 2,
                                borderRadius: 3,
                                width: "40%",
                                backgroundColor: theme.palette.info.light,
                                color: theme.palette.info.contrastText,
                            }}
                        >
                            Start new
                        </Button>
                    </Box>
                </Box>
            </Container>
        );
    }


}

export default Review;